<template>
  <div class="calendario-mensual">
    <!-- HEADER CON NAVEGACIÓN -->
    <div class="header-mensual">
      <button @click="mesAnterior" class="btn-nav">‹</button>
      <div class="mes-info">
        <span class="mes-nombre">{{ mesNombreFormateado }}</span>
      </div>
      <button @click="mesSiguiente" class="btn-nav">›</button>
    </div>

    <!-- TABLA MENSUAL -->
    <div class="tabla-mensual">
      <!-- CABECERA CON DÍAS DE SEMANA -->
      <div class="cabecera">
        <div
          v-for="dia in DIAS_SEMANA"
          :key="dia.clave"
          class="dia-semana"
          :class="{ 'dia-semana-inactivo': !diasActivos[dia.clave] }"
        >
          {{ dia.label }}
        </div>
      </div>

      <!-- GRILLA DE DÍAS -->
      <div class="grilla">
        <div 
          v-for="(día, idx) in diasDelMes" 
          :key="idx" 
          class="celda-dia"
          :class="{ 'otro-mes': !día.esDelMes, 'hoy': día.esHoy, 'dia-inactivo': !esDiaActivo(día.fecha) }"
          @click="abrirModalDia(día)"
        >
          <div class="numero-dia">{{ día.numero }}</div>
          <div class="eventos-dia">
            <div 
              v-for="(turno, i) in obtenerTurnosDia(día.fecha).slice(0, 2)" 
              :key="i"
              class="evento-mini"
              :style="{ backgroundColor: colorPorEstado(turno.estado) }"
            >
              <span class="evento-texto">{{ turno.paciente }}</span>
            </div>
            <div v-if="obtenerTurnosDia(día.fecha).length > 2" class="mas-eventos">
              +{{ obtenerTurnosDia(día.fecha).length - 2 }} más
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL DETALLE DÍA -->
    <div v-if="diaSeleccionado" class="modal-overlay" @click.self="cerrarModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="fecha-titulo" @click="verDiaCompleto" title="Ver el día completo">
            {{ formatearFechaCompleta(diaSeleccionado.fecha) }} ›
          </h3>
          <button class="btn-close" @click="cerrarModal">×</button>
        </div>

        <!-- Día que el usuario no atiende: se avisa y no se ofrecen horarios libres. Los
             turnos que ya existan ese día se siguen viendo y se pueden abrir. -->
        <p v-if="!diaAbiertoActivo" class="aviso-dia-bloqueado-modal">{{ avisoDiaBloqueado }}</p>

        <!-- Compacto: solo las citas agendadas -->
        <div v-if="!mostrarSlotsLibres" class="lista-turnos-modal">
          <div
            v-for="turno in obtenerTurnosDia(diaSeleccionado.fecha)"
            :key="turno.id"
            class="turno-modal-item clickable"
            :class="{ 'item-bloqueo': turno.tipo === 'bloqueo' }"
            :style="{ borderLeftColor: colorPorEstado(turno.estado) }"
            @click="abrirTurno(turno)"
          >
            <div class="hora-modal">{{ turno.inicio }}</div>
            <div class="info-modal">
              <strong>{{ turno.paciente }}</strong>
              <span>{{ turno.motivo }}</span>
            </div>
            <span class="chevron-modal">›</span>
          </div>
          <p v-if="obtenerTurnosDia(diaSeleccionado.fecha).length === 0" class="sin-turnos">
            No hay turnos agendados.
          </p>
        </div>

        <!-- Expandido: todos los horarios. Tocar uno libre agenda a esa hora. -->
        <div v-else class="lista-slots-modal">
          <div
            v-for="slot in slotsDelDiaSeleccionado"
            :key="slot.hora"
            class="slot-modal"
            :class="[slot.turno ? 'slot-ocupado clickable' : 'slot-libre', { 'item-bloqueo': slot.turno && slot.turno.tipo === 'bloqueo', 'slot-fuera-horario': slot.fuera }]"
            :style="slot.turno ? { borderLeftColor: colorPorEstado(slot.turno.estado) } : {}"
            @click="slot.turno ? abrirTurno(slot.turno) : agendarEnSlot(slot.hora)"
          >
            <span class="slot-hora">{{ slot.hora }}</span>
            <template v-if="slot.turno">
              <span class="slot-paciente">{{ slot.turno.paciente }}</span>
              <!-- Turno fuera del horario configurado: se lista igual (si no sería invisible). -->
              <span v-if="slot.fuera" class="slot-marca-fuera">fuera de horario</span>
              <span class="chevron-modal">›</span>
            </template>
            <template v-else>
              <span class="slot-disponible">Disponible</span>
              <span class="slot-mas">+</span>
            </template>
          </div>
        </div>

        <button v-if="diaAbiertoActivo" class="btn-toggle-disponibles" @click="mostrarDisponibles = !mostrarDisponibles">
          {{ mostrarDisponibles ? '← Ver solo agendados' : 'Ver horarios disponibles' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useScrollLock } from '@/composables/useScrollLock';
import { turnoCubre, diaEnPlural, DIAS_SEMANA } from '@/utils/horarios';
import { useHorarioAgenda } from '@/composables/useHorarioAgenda';

const { slotsPara, estaFueraDeHorario, diasActivos, esDiaActivo } = useHorarioAgenda();

const COLORES_ESTADO = {
  'pendiente':  '#678AB2',
  'confirmado': '#22c55e',
  'en curso':   '#f59e0b',
  'atendido':   '#a855f7',
  'no asiste':  '#92400e',
  'anulado':    '#ef4444',
  'bloqueo':    '#9ca3af',
};
function colorPorEstado(estado) {
  return COLORES_ESTADO[estado] || '#678AB2';
}

const props = defineProps({
  turnos: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['abrirTurno', 'irADia', 'nuevaCita']);

const fechaActual = ref(new Date());

const diaSeleccionado = ref(null);
const mostrarDisponibles = ref(false);

// ¿El día que se abrió en el modal es uno de los que atiende?
const diaAbiertoActivo = computed(() =>
  diaSeleccionado.value ? esDiaActivo(diaSeleccionado.value.fecha) : true,
);

// En un día bloqueado nunca se muestran los huecos libres, aunque el usuario haya dejado
// activado "Ver horarios disponibles" en otro día.
const mostrarSlotsLibres = computed(() => mostrarDisponibles.value && diaAbiertoActivo.value);

const avisoDiaBloqueado = computed(() =>
  diaSeleccionado.value
    ? `No atendés ${diaEnPlural(diaSeleccionado.value.fecha)}. Podés cambiarlo en Ajustes › Horario de trabajo.`
    : '',
);

// Bloquear el scroll del fondo mientras el modal del día está abierto.
useScrollLock(() => !!diaSeleccionado.value);

// Cada horario del día abierto, con su cita (si empieza ahí) o libre. El rango es el horario
// de trabajo configurado en Ajustes (por defecto 09:00 a 22:30), igual que la vista Día.
const slotsDelDiaSeleccionado = computed(() => {
  if (!diaSeleccionado.value) return [];
  const delDia = obtenerTurnosDia(diaSeleccionado.value.fecha);
  // Ocultar los slots CUBIERTOS por una cita más larga (ej: 9:30 dentro de un turno de 9 a 10):
  // no son agendables. Solo se listan los que empiezan un turno o están realmente libres.
  const slots = [];
  for (const hora of slotsPara(delDia)) {
    const turno = delDia.find((t) => t.inicio === hora) || null;
    if (!turno && delDia.some((t) => turnoCubre(t, hora))) continue;
    slots.push({ hora, turno, fuera: estaFueraDeHorario(hora) });
  }
  return slots;
});

const mesNombreFormateado = computed(() => {
  const dateString = fechaActual.value.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  return dateString.replace(/ de /g, ' ').replace(/ del /g, ' ');
});

// Obtener el primer día del mes
const primerDiaDelMes = computed(() => {
  return new Date(fechaActual.value.getFullYear(), fechaActual.value.getMonth(), 1);
});

// Obtener el lunes de la semana anterior si es necesario
const primerLunes = computed(() => {
  const fecha = new Date(primerDiaDelMes.value);
  const dia = fecha.getDay();
  const diff = fecha.getDate() - (dia === 0 ? 6 : dia - 1);
  return new Date(fecha.setDate(diff));
});

function fechaLocalStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Generar la grilla de días
const diasDelMes = computed(() => {
  const dias = [];
  const fecha = new Date(primerLunes.value);
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  // Generar 42 días (6 semanas)
  for (let i = 0; i < 42; i++) {
    const fechaStr = fechaLocalStr(fecha);
    const esDelMes = fecha.getMonth() === fechaActual.value.getMonth();
    const esHoy = fecha.getTime() === hoy.getTime();

    dias.push({
      numero: fecha.getDate(),
      fecha: fechaStr,
      esDelMes,
      esHoy
    });

    fecha.setDate(fecha.getDate() + 1);
  }

  return dias;
});

// Obtener turnos de un día específico
function obtenerTurnosDia(fecha) {
  return props.turnos
    .filter(t => {
      const turnoFecha = t.fecha.split('T')[0];
      return turnoFecha === fecha && t.estado !== 'eliminada';
    })
    .sort((a, b) => {
      // Ordenar por hora de inicio (formato "HH:MM")
      const inicioA = a.inicio || '';
      const inicioB = b.inicio || '';
      return inicioA.localeCompare(inicioB);
    });
}

// Navegación de meses
function mesAnterior() {
  fechaActual.value = new Date(fechaActual.value.getFullYear(), fechaActual.value.getMonth() - 1);
}

function mesSiguiente() {
  fechaActual.value = new Date(fechaActual.value.getFullYear(), fechaActual.value.getMonth() + 1);
}

function abrirModalDia(dia) {
  diaSeleccionado.value = dia;
  mostrarDisponibles.value = false;
}

function cerrarModal() {
  diaSeleccionado.value = null;
}

// Abrir una cita desde el mes para verla/editarla (cierra el detalle del día).
function abrirTurno(turno) {
  emit('abrirTurno', turno);
  cerrarModal();
}

// Tocar la fecha del título: ir a la vista Día completa de ese día.
function verDiaCompleto() {
  if (!diaSeleccionado.value) return;
  emit('irADia', diaSeleccionado.value.fecha);
  cerrarModal();
}

// Tocar un horario libre: abrir el formulario de nueva cita con esa fecha y hora.
function agendarEnSlot(hora) {
  if (!diaSeleccionado.value) return;
  emit('nuevaCita', { fecha: diaSeleccionado.value.fecha, hora });
  cerrarModal();
}

function formatearFechaCompleta(fechaStr) {
  if (!fechaStr) return '';
  const [y, m, d] = fechaStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
}
</script>

<style scoped>
.calendario-mensual {
  padding: 4px; /* Menos padding en móvil para ganar espacio */
  background: white;
}

.header-mensual {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding: 12px 0;
  border-bottom: 2px solid #eee;
}

.btn-nav {
  background: none;
  border: none;
  font-size: 24px;
  color: #678AB2;
  cursor: pointer;
  padding: 0 8px;
}

.mes-nombre {
  font-weight: 600;
  color: #333;
  font-size: 14px;
  text-transform: capitalize;
}

.tabla-mensual {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cabecera {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  background: #f9f9f9;
  padding: 8px 0;
  border-radius: 6px;
  margin-bottom: 6px;
}

.dia-semana {
  text-align: center;
  font-weight: 600;
  color: #666;
  font-size: 10px;
  text-transform: uppercase;
}

.grilla {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.celda-dia {
  min-height: 60px; /* Altura mínima reducida en móvil */
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 2px; /* Padding reducido */
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 0;
  overflow: hidden;
}

.celda-dia:hover {
  background: #f0f0f0;
  border-color: #678AB2;
}

.celda-dia.otro-mes {
  opacity: 0.4;
  background: white;
}

/* Día que el usuario marcó como "no atiendo". Se ve apagado pero sigue siendo clickeable:
   la idea es que no invite, no que impida agendar un sábado excepcional. Va ANTES de .hoy
   para que el día de hoy conserve su celeste aunque caiga en un día apagado. */
.celda-dia.dia-inactivo {
  background: #f1f2f4;
  border-color: #e6e7e9;
}

.celda-dia.dia-inactivo .numero-dia {
  color: #a3a8b0;
}

.dia-semana-inactivo {
  color: #b6bbc3;
}

.celda-dia.hoy {
  background: #e3f2fd;
  border-color: #678AB2;
  font-weight: 600;
}

.numero-dia {
  font-weight: 600;
  color: #333;
  font-size: 12px;
  margin-bottom: 4px;
}

.celda-dia.hoy .numero-dia {
  color: #678AB2;
}

.eventos-dia {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.evento-mini {
  color: white;
  padding: 2px 3px;
  border-radius: 3px;
  font-size: 8px;
  font-weight: 500;
  overflow: hidden;
}

.evento-texto {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

/* Tablet */
@media (min-width: 768px) {
  .calendario-mensual {
    padding: 16px;
  }

  .header-mensual {
    margin-bottom: 20px;
    padding: 16px 0;
  }

  .mes-nombre {
    font-size: 16px;
  }

  .cabecera {
    gap: 6px;
    padding: 10px 6px;
    margin-bottom: 8px;
  }

  .dia-semana {
    font-size: 11px;
  }

  .grilla {
    gap: 6px;
  }

  .celda-dia {
    min-height: 85px;
    border-radius: 8px;
    padding: 8px;
    padding: 6px;
  }

  .numero-dia {
    font-size: 14px;
    margin-bottom: 6px;
  }

  .evento-mini {
    padding: 4px 6px;
    font-size: 9px;
  }
}

/* Desktop */
@media (min-width: 1024px) {
  .calendario-mensual {
    padding: 20px;
  }

  .header-mensual {
    margin-bottom: 24px;
    padding: 16px 0;
  }

  .btn-nav {
    padding: 0 12px;
  }

  .mes-nombre {
    font-size: 18px;
  }

  .cabecera {
    gap: 8px;
    padding: 12px 8px;
    margin-bottom: 10px;
  }

  .dia-semana {
    font-size: 12px;
  }

  .grilla {
    gap: 8px;
  }

  .celda-dia {
    min-height: 100px;
    border-radius: 8px;
    padding: 10px;
  }

  .numero-dia {
    font-size: 14px;
    margin-bottom: 6px;
    text-align: left;
  }

  .evento-mini {
    padding: 4px 6px;
    border-radius: 4px;
    font-size: 10px;
  }
}

.mas-eventos {
  font-size: 10px;
  color: #666;
  text-align: center;
  margin-top: 2px;
  font-weight: 600;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
}
.modal-content {
  background: white;
  width: 90%;
  max-width: 400px;
  border-radius: 12px;
  padding: 20px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  font-family: system-ui, sans-serif;
  display: flex;
  flex-direction: column;
}
.modal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.modal-header h3 { margin: 0; font-size: 18px; color: #678AB2; text-transform: capitalize; }
.btn-close { 
  background: #f1f3f5; 
  border: none; 
  width: 32px; height: 32px; border-radius: 50%; 
  font-size: 20px; cursor: pointer; color: #555;
  display: flex; align-items: center; justify-content: center;
  transition: background 0.2s;
}
.btn-close:hover { background: #e9ecef; }

.lista-turnos-modal { display: flex; flex-direction: column; gap: 8px; }
.turno-modal-item {
  display: flex; gap: 12px; padding: 10px;
  background: #f9f9f9; border-radius: 8px;
  border-left: 4px solid #ccc;
  align-items: center;
}
.turno-modal-item.clickable { cursor: pointer; transition: background 0.15s; }
.turno-modal-item.clickable:hover { background: #eef2f7; }
.chevron-modal { margin-left: auto; color: #9aa5b1; font-size: 22px; font-weight: 700; line-height: 1; padding-left: 6px; }
.hora-modal { font-weight: 700; color: #555; font-size: 14px; min-width: 45px; }
.info-modal { display: flex; flex-direction: column; }
.info-modal strong { font-size: 14px; color: #333; }
.info-modal span { font-size: 12px; color: #666; }
.sin-turnos { text-align: center; color: #999; font-style: italic; margin-top: 10px; }

/* Título de fecha clickeable (lleva al día completo) */
.fecha-titulo { cursor: pointer; }
.fecha-titulo:hover { text-decoration: underline; }

/* Lista de horarios (toggle "Ver horarios disponibles") */
.lista-slots-modal { display: flex; flex-direction: column; gap: 6px; }
.slot-modal { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: 8px; font-size: 14px; }
.slot-ocupado { background: #f9f9f9; border-left: 4px solid #ccc; }
.slot-libre { background: #e3f0fc; border: 1px dashed #8fbce6; color: #3b78bd; cursor: pointer; transition: background 0.15s; }
.slot-libre:hover { background: #d2e6f9; }
/* Bloqueo/descanso en las listas del modal del día (compacta y expandida): fondo grisáceo.
   Selector con 2 clases para ganarle a .slot-ocupado / .turno-modal-item sin depender del orden. */
.turno-modal-item.item-bloqueo,
.slot-modal.item-bloqueo { background: #e6e8eb; }
.slot-modal.clickable { cursor: pointer; transition: background 0.15s; }
.slot-modal.clickable:hover { background: #eef2f7; }
/* En hover, mantener el gris del bloqueo (un poco más oscuro) en vez del azulado. */
.turno-modal-item.item-bloqueo:hover,
.slot-modal.item-bloqueo:hover { background: #dcdfe3; }
.slot-hora { font-weight: 700; color: #555; min-width: 45px; }
.slot-paciente { color: #333; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.slot-disponible { color: #3b78bd; }
.aviso-dia-bloqueado-modal {
  margin: 0 0 12px; padding: 9px 12px;
  background: #f1f2f4; border-left: 3px solid #9ca3af; border-radius: 0 8px 8px 0;
  color: #4b5563; font-size: 12.5px; line-height: 1.4;
}
/* Turno fuera del horario configurado. 2 clases para ganarle a .slot-ocupado sin depender
   del orden de las reglas, igual que .item-bloqueo. */
.slot-modal.slot-fuera-horario { background: #fffbeb; border-left-color: #f59e0b; }
.slot-marca-fuera {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  color: #92400e; background: #fef3c7; padding: 2px 6px; border-radius: 4px; white-space: nowrap;
}
.slot-mas { margin-left: auto; font-size: 18px; font-weight: 700; color: #3b78bd; line-height: 1; }

/* Botón toggle disponibles */
.btn-toggle-disponibles {
  margin-top: 14px; width: 100%; padding: 11px;
  background: #eaf1ff; border: none; border-radius: 10px;
  color: #678AB2; font-size: 14px; font-weight: 600; cursor: pointer;
  transition: background 0.2s;
}
.btn-toggle-disponibles:hover { background: #dbe7fb; }
</style>
